import { useCallback, useMemo } from 'react';
import type { KBEdge, KBNode } from '../types';

interface UseLanguageFilterArgs {
  nodes: KBNode[];
  edges: KBEdge[];
  selectedLanguageIds?: Set<string>;
}

export function useLanguageFilter({ nodes, edges, selectedLanguageIds }: UseLanguageFilterArgs) {
  const toolLanguages = useMemo(() => {
    const languageIds = new Set(nodes.filter((n) => n.kind === 'language').map((n) => n.id));
    const map: Record<string, Set<string>> = {};
    const link = (toolId: string, langId: string) => {
      if (!map[toolId]) map[toolId] = new Set();
      map[toolId].add(langId);
    };
    for (const e of edges) {
      if (e.type !== 'supports_language' && e.type !== 'language') continue;
      if (languageIds.has(e.target)) link(e.source, e.target);
      else if (languageIds.has(e.source)) link(e.target, e.source);
    }
    return map;
  }, [edges, nodes]);

  const isLanguageMatch = useCallback(
    (toolId: string): boolean => {
      if (!selectedLanguageIds || selectedLanguageIds.size === 0) return true;
      if (selectedLanguageIds.has(toolId)) return true;
      const langs = toolLanguages[toolId];
      if (!langs || langs.size === 0) return true;
      for (const lang of langs) {
        if (selectedLanguageIds.has(lang)) return true;
      }
      return false;
    },
    [selectedLanguageIds, toolLanguages],
  );

  return { isLanguageMatch };
}
